import React, { useEffect, useRef, useState } from "react";
import Map from "@zeliot/core/base/modules/TrackingControls/Maps/Map";

// Import de l'image du bus
import yellowBusIcon from "src/assets/images/png/yellow_bus.png";

/**
 * ReplayMapLite - Version Minimaliste avec Bus Jaune (Sans bouton plein écran)
 */
const ReplayMapLite = ({
  positions = [],
  currentTime = 0,
  duration = 1,
  showViolations = true,
}) => {
  const [map, setMap] = useState(null);
  const tripPolylineRef = useRef(null);
  const vehicleMarkerRef = useRef(null);
  const startMarkerRef = useRef(null);
  const endMarkerRef = useRef(null);
  const violationMarkersRef = useRef([]);

  const hasGoogle = typeof window !== "undefined" && !!window.google;

  useEffect(() => {
    if (map && positions.length > 0) {
      const firstPos = positions[0];
      map.setCenter({ lat: firstPos.lat, lng: firstPos.lng });
      map.setZoom(15);
      
      // Désactiver le scroll et le zoom
      map.setOptions({
        scrollwheel: false,
        draggable: true,
        zoomControl: true,
        minZoom: 12,
        maxZoom: 18
      });
      
      drawTripPath();
      createStartEndMarkers();
      if (showViolations) createViolationMarkers();
    }
    return () => cleanupMarkers();
  }, [map, positions]);

  useEffect(() => {
    if (map && positions.length > 0) {
      updateVehiclePosition();
    }
  }, [currentTime, positions, map]);

  const drawTripPath = () => {
    if (!map || positions.length === 0) return;
    if (tripPolylineRef.current) tripPolylineRef.current.setMap(null);
    const path = positions.map((pos) => new window.google.maps.LatLng(pos.lat, pos.lng));
    const polyline = new window.google.maps.Polyline({
      path,
      geodesic: true,
      strokeColor: "#f59e0b",
      strokeOpacity: 0.6,
      strokeWeight: 5,
    });
    polyline.setMap(map);
    tripPolylineRef.current = polyline;
  };

  const createStartEndMarkers = () => {
    if (!map || positions.length === 0) return;
    if (startMarkerRef.current) startMarkerRef.current.setMap(null);
    if (endMarkerRef.current) endMarkerRef.current.setMap(null);

    const startPos = positions[0];
    const endPos = positions[positions.length - 1];

    startMarkerRef.current = new window.google.maps.Marker({
      position: { lat: startPos.lat, lng: startPos.lng },
      map,
      icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#22c55e", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2 },
      title: "Départ"
    });

    endMarkerRef.current = new window.google.maps.Marker({
      position: { lat: endPos.lat, lng: endPos.lng },
      map,
      icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 8, fillColor: "#ef4444", fillOpacity: 1, strokeColor: "#ffffff", strokeWeight: 2 },
      title: "Arrivée"
    });
  };

  const createViolationMarkers = () => {
    if (!map) return;
    violationMarkersRef.current.forEach((marker) => marker.setMap(null));
    violationMarkersRef.current = [];
    positions.filter((pos) => pos.isViolation).forEach((pos) => {
      const marker = new window.google.maps.Marker({
        position: { lat: pos.lat, lng: pos.lng },
        map,
        icon: { path: window.google.maps.SymbolPath.CIRCLE, scale: 5, fillColor: "#ef4444", fillOpacity: 0.7, strokeColor: "#ffffff", strokeWeight: 1 },
      });
      violationMarkersRef.current.push(marker);
    });
  };

  const updateVehiclePosition = () => {
    if (!map || positions.length === 0) return;
    if (vehicleMarkerRef.current) vehicleMarkerRef.current.setMap(null);

    const progress = Math.min(currentTime / (duration * 60), 1);
    const index = Math.floor(progress * (positions.length - 1));
    const currentPos = positions[index];

    if (!currentPos) return;

    vehicleMarkerRef.current = new window.google.maps.Marker({
      position: { lat: currentPos.lat, lng: currentPos.lng },
      map,
      icon: {
        url: yellowBusIcon,
        scaledSize: new window.google.maps.Size(50, 25),
        anchor: new window.google.maps.Point(25, 12),
      },
      zIndex: 1000,
    });
  };

  const cleanupMarkers = () => {
    if (tripPolylineRef.current) tripPolylineRef.current.setMap(null);
    if (vehicleMarkerRef.current) vehicleMarkerRef.current.setMap(null);
    if (startMarkerRef.current) startMarkerRef.current.setMap(null);
    if (endMarkerRef.current) endMarkerRef.current.setMap(null);
    violationMarkersRef.current.forEach((marker) => marker.setMap(null));
  };

  return (
    <div className="relative w-full h-full bg-slate-100">
      <div className="w-full h-full">
        {hasGoogle ? (
          <Map
            google={window.google}
            setMap={setMap}
            zoom={15}
            containerClassName="replay-map-canvas"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-xs font-bold text-slate-400 uppercase tracking-widest">
            Chargement Google Maps...
          </div>
        )}
      </div>
    </div>
  );
};

export default ReplayMapLite;
